/**
 * Клетка матрицы объясняет себя.
 *
 * Матрица — это четыре цвета на сетке 50×8, и до сих пор цвет был всем, что она
 * говорила: нажатие не делало ничего — обработчик в `viz.tsx` объявлен, а экран
 * его не передавал. Красная клетка сообщала «не назвали» и молчала о
 * единственном вопросе, который за ней следует: а что движок вообще ответил.
 *
 * Окно отвечает ровно на него и ровно по одной клетке — движок, дата замера,
 * результат словами, место и тональность по каждому прогону, выжимка ответа и
 * полный текст по нажатию. Сводки по вопросу и по движку живут на своих
 * экранах: вторая их копия здесь разъехалась бы с ними на первой же правке.
 *
 * Слово о состоянии здесь СОБИРАЕТСЯ ПО ФАКТУ, а не берётся из подписи клетки.
 * Клетка знает четыре состояния, а замер знает, сколько прогонов в ней удалось,
 * и при одном удавшемся прогоне «назван в обоих прогонах» — враньё. Общая
 * подпись на четыре состояния сказать этого не может, поэтому фразу собирает
 * `cellWord` и отдаёт её и в матрицу, и сюда: два описания одного состояния
 * разъезжаются.
 */

import { useState } from 'react';

import { Dialog } from './Dialog';
import { ErrorNotice, Skeleton } from './state';
import { useRead } from './query';
import { parserKind, sentimentWord } from './labels';
import { t } from '../i18n';
import type { Cell, CellTry } from './cells';
import { dateNumeric, dateProse } from '@shared/src/format';
import type { components } from '../api/schema';

type AnswerDetail = components['schemas']['AnswerDetail'];

/**
 * Сколько прогонов на клетку планировал замер. Тип берётся из контракта, а не
 * переписывается числами: перечисление живёт в `openapi.yaml`, и вторая его
 * копия здесь разъехалась бы с ним молча.
 */
export type PerCell = components['schemas']['Run']['runs_per_cell'];

/**
 * Состояние клетки словами — с поправкой на то, сколько прогонов удалось.
 *
 * Фраза короткая намеренно: она стоит подписью клетки, повторяется в каждой
 * ячейке таблицы под матрицей и читается вслух по сетке 50×8. Объяснение
 * подробностями живёт в окне клетки, где оно одно.
 *
 * Удавшихся прогонов бывает меньше запланированных: движок отвечает не всегда,
 * и тогда клетка стоит закрашенной по ОДНОМУ ответу. Согласия прогонов в ней
 * нет, и говорить о нём нельзя — ни в подписи клетки, ни в окне. Один прогон на
 * клетку — это бесплатный аудит: там согласия нет по построению, и упоминать
 * прогоны незачем вовсе.
 */
export function cellWord(cell: Cell | undefined, perCell: PerCell): string {
  const tries = cell?.tries ?? 0;
  const hits = cell?.hits ?? 0;

  if (tries === 0) return t('not-measured.inline');
  if (perCell === 1) return hits > 0 ? t('cell.word.named') : t('cell.word.not-named');
  // Недостающий прогон называется первым: без него «назван» читается как
  // подтверждённое согласием двух прогонов, а подтверждать было нечем.
  if (tries < perCell) {
    return hits > 0
      ? t('cell.word.named-one-pending')
      : t('cell.word.not-named-one-pending');
  }
  if (hits === 0) return t('cell.word.not-named');
  if (hits === tries) return t('cell.word.both');
  return t('cell.word.one-of-two');
}

/** Что вышло в одном прогоне: назван или нет, на каком месте, с какой тональностью. */
function tryWord(attempt: CellTry): string {
  if (!attempt.mentioned) return t('cell.try.not-named');
  return [
    t('cell.try.named'),
    attempt.position === null
      ? t('cell.try.no-position')
      : t('cell.try.position', { n: attempt.position }),
    ...(attempt.sentiment
      ? [t('cell.try.sentiment', { word: sentimentWord(attempt.sentiment) })]
      : []),
  ].join(', ');
}

/**
 * Окно клетки.
 *
 * Клетки может не быть вовсе — это «не измерено», и оно тоже состояние: движок
 * по этому вопросу в этом замере не ответил. Пустое окно на такой клетке
 * читалось бы как поломка кабинета.
 */
export function CellCard({
  question,
  engine,
  cell,
  perCell,
  when,
  complete,
  onClose,
}: {
  question: string;
  engine: string;
  cell: Cell | undefined;
  perCell: PerCell;
  /** День закрытия замера. `null` — замер завершён, а дня у него нет. */
  when: string | null;
  /**
   * Прочитан ли замер до конца. `false` — сработал предохранитель страниц, и
   * недостающие прогоны объясняются нашим чтением, а не молчанием движка.
   * Приписать движку свой пробел — та же ошибка, из-за которой матрица
   * показывала «не измерено» по измеренным вопросам.
   */
  complete: boolean;
  onClose: () => void;
}) {
  const attempts = cell?.attempts ?? [];
  const cut = complete ? '' : ` ${t('cell.cut')}`;

  return (
    <Dialog
      open
      title={t('cell.title')}
      onClose={onClose}
      wide
      // Выход мышью обязателен: закрытия кликом по подложке у наших окон нет
      // намеренно, и без кнопки остался бы один выход — Escape.
      footer={
        <button className="btn btn-ghost" type="button" onClick={onClose}>
          {t('cell.close')}
        </button>
      }
    >
      <p className="dlg-lead">{question}</p>

      <p className="stamp">
        {engine} ·{' '}
        {when ? t('cell.run-dated', { date: dateProse(when) }) : t('cell.run-latest')} ·{' '}
        {cellWord(cell, perCell)}
      </p>

      {attempts.length === 0 ? (
        // «Не измерено» — не ноль и не пустая клетка. Человек, увидевший
        // штриховку, вправе знать, что она означает пробел замера, а не
        // отсутствие бренда в ответе. Больше сказанного мы не знаем: задавали
        // ли вопрос движку, отсюда не видно — виден только ответ, которого нет.
        <p className="stamp block">
          {t('cell.empty')}
          {cut}
        </p>
      ) : (
        <div className="items">
          {attempts.map((attempt) => (
            <Attempt key={attempt.id} attempt={attempt} perCell={perCell} />
          ))}
        </div>
      )}

      {attempts.length > 0 && attempts.length < perCell ? (
        <p className="stamp">
          {t('cell.partial', { n: attempts.length, total: perCell })}
          {cut}
        </p>
      ) : null}
    </Dialog>
  );
}

/** Один прогон клетки: чем он кончился и что именно было в ответе. */
function Attempt({ attempt, perCell }: { attempt: CellTry; perCell: PerCell }) {
  const [full, setFull] = useState(false);

  return (
    <article className="item">
      <div className="item-mark">
        <b>
          {attempt.no === null
            ? t('cell.attempt.unknown')
            : perCell === 1
              ? t('cell.attempt.single')
              : t('cell.attempt.of', { no: attempt.no, total: perCell })}
        </b>
        <span className="stamp">{dateNumeric(attempt.at)}</span>
      </div>

      <div className="item-body">
        <h3>{tryWord(attempt)}</h3>

        {/* Место ставит только разбор нейросетью: запасной поиск находит
            вхождение имени и о порядке в списке не знает ничего. Без этой
            строки пустое место читается как «назвали последними». */}
        {attempt.parser === 'text_match' ? (
          <p>
            {attempt.mentioned
              ? t('cell.parser.text-match-found', { kind: parserKind('text_match') })
              : t('cell.parser.text-match-not-found', { kind: parserKind('text_match') })}
          </p>
        ) : null}

        {/* Выжимки может не быть, а текст при этом быть: договор объявляет
            `snippet` необязательным, а полный текст держит отдельной ручкой.
            Поэтому по пустой выжимке не утверждается ничего — ни про срок
            хранения, ни про пустой ответ: спросить дешевле, чем соврать. */}
        {full ? (
          <FullAnswer id={attempt.id} />
        ) : attempt.snippet ? (
          <p>{attempt.snippet}</p>
        ) : null}

        {attempt.model ? (
          <p className="stamp">{t('cell.model', { model: attempt.model })}</p>
        ) : null}
      </div>

      <div className="item-act">
        {!full ? (
          <button
            className="btn btn-ghost btn-sm"
            type="button"
            onClick={() => setFull(true)}
          >
            {t('cell.full-answer')}
          </button>
        ) : null}
      </div>
    </article>
  );
}

/**
 * Полный текст ответа — отдельным запросом и только по нажатию.
 *
 * В выжимке лежат первые 280 знаков, а ответ движка бывает на страницу: возить
 * их все на сетку 50×8 значит тянуть мегабайты ради одной клетки, которую
 * откроют.
 */
function FullAnswer({ id }: { id: string }) {
  const answer = useRead<AnswerDetail>(`/answers/${id}`);

  if (answer.kind === 'loading') return <Skeleton rows={2} />;
  if (answer.kind === 'failed') return <ErrorNotice error={answer.error} />;

  // Вычищенный текст — это `null` договора, и только он. Пустая строка говорит
  // другое: движок ответил пустотой, и разбор эту пустоту видел. Выдать её за
  // срок хранения значит объяснить сбой источника нашей уборкой.
  const body = answer.data.body ?? null;
  if (body === null) {
    return <p className="stamp">{t('cell.retention')}</p>;
  }
  return body === '' ? (
    <p className="stamp">{t('cell.empty-answer')}</p>
  ) : (
    // Текст движка печатается КАК ПРИШЁЛ: списки и абзацы в нём несут смысл, а
    // обычный абзац схлопывает переводы строк в пробелы. Длинный адрес при этом
    // не имеет права раздвинуть окно на узком экране, поэтому перенос
    // разрешён и внутри слова.
    <p className="answer-body">{body}</p>
  );
}
